/**
 * Shown when a planning page cannot resolve a plan cycle.
 * Fetches all plan cycles (any status) and lets the user pick one,
 * or provides a CTA to create the first cycle.
 *
 * Per build contract §5: readiness surfaces must name the missing input
 * and provide a remediation surface — not just a dead-end message.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, Plus } from 'lucide-react';
import { listPlanCycles } from '../../lib/planningService';
import type { PlanCycle } from '../../types/planning';
import { PlanningListSkeleton } from './PlanningListSkeleton';

interface Props {
  /** Called when the user selects a cycle to view. */
  onSelectCycle: (cycleId: string) => void;
}

export function NoPlanCycleState({ onSelectCycle }: Props) {
  const [cycles, setCycles] = useState<PlanCycle[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    listPlanCycles().then(({ data }) => {
      setCycles(data ?? []);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <PlanningListSkeleton className="h-48" />;

  if (cycles.length === 0) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8">
        <div className="flex items-start gap-4">
          <CalendarRange className="h-8 w-8 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[var(--text-pri)] mb-1">No plan cycles found</p>
            <p className="text-sm text-[var(--text-ter)] mb-4">
              This view uses the classic plan cycle system, which requires a published Priority List or
              Item Pool as input. You have two options:
            </p>
            <div className="space-y-3">
              <div className="p-3 bg-primary-50 border border-primary-100">
                <p className="text-sm font-medium text-primary-900">Planning Engine (recommended)</p>
                <p className="text-xs text-primary-700 mt-0.5 mb-2">
                  Scenario-based planning — works standalone, no upstream prioritization required.
                </p>
                <Link
                  to="/planning/engine"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Open Planning Engine
                </Link>
              </div>
              <div className="p-3 bg-[var(--surface)] border border-[var(--rule)]">
                <p className="text-sm font-medium text-[var(--text-pri)]">Plan of Record</p>
                <p className="text-xs text-[var(--text-ter)] mt-0.5 mb-2">
                  Create a Plan of Record tied to a published Priority List from Prioritization.
                </p>
                <Link
                  to="/planning/plan-of-record"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] rounded text-xs font-medium hover:bg-[var(--surface)]"
                >
                  View Plan of Record →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]">
        <p className="font-semibold text-[var(--text-pri)]">Select a plan cycle</p>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          No active plan cycle. Pick one below to view its data.
        </p>
      </div>
      <ul className="divide-y divide-[var(--rule)]">
        {cycles.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelectCycle(c.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--surface)]"
            >
              <div>
                <p className="font-medium text-[var(--text-pri)]">{c.name || `Plan cycle ${c.id.slice(0, 8)}`}</p>
              </div>
              <span
                className={`ml-4 flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                  c.status === 'active'
                    ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                    : c.status === 'draft'
                    ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                    : c.status === 'closed'
                    ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                    : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                }`}
              >
                {c.status}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
